import React from "react";

import { useAuth } from "@wc/domain/authn";
import {
  isFirstDayOfWeek,
  getCurrentWeekKey,
  getPreviousWeekKey,
} from "@wc/domain/dates";
import { useOrg } from "@wc/domain/organizations";

import {
  checkWeekReviewStatus,
  markPreviousWeekReviewed,
  saveGoal,
} from "../core/goals-service";

/**
 * @typedef {object} Goal
 * @property {string} id The goal id
 * @property {string} text The goal text
 * @property {boolean} completed Whether the goal is completed
 * @property {string} organizationId The organization this goal belongs to
 * @property {string} teamId The team this goal is associated with
 */

/**
 * @typedef {object} UseWeekReviewResult
 * @property {boolean} isOpen Whether the review modal is open
 * @property {Goal[]} previousWeekGoals Goals from the previous week
 * @property {string} previousWeekKey The previous week's date key
 * @property {boolean} loading Whether data is being loaded
 * @property {boolean} allComplete Whether all previous goals are complete
 * @property {() => Promise<void>} onClose Handler to close the modal
 * @property {(goalId: string, completed: boolean) => Promise<void>} onGoalToggle Handler for toggling goal completion
 */

/**
 * Hook to manage the week review modal state
 * Shows on the first day of a new week if there are unreviewed goals from last week
 * @param {number} weekStartsOn Day the week starts on (0=Sun, 1=Mon, etc. Default: 1)
 * @returns {UseWeekReviewResult} The week review state and handlers
 */
export default function useWeekReview(weekStartsOn = 1) {
  const authData = useAuth();
  const orgData = useOrg();

  const [isOpen, setIsOpen] = React.useState(false);
  const [previousWeekGoals, setPreviousWeekGoals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const currentWeekKey = getCurrentWeekKey();
  const previousWeekKey = getPreviousWeekKey(currentWeekKey);

  const userId = authData.isLoggedIn() ? authData.getCurrentUser().id : null;
  const organizationId = orgData?.org?.id;

  React.useEffect(() => {
    const checkReviewStatus = async () => {
      if (!userId || !organizationId) {
        setLoading(false);
        return;
      }

      if (!isFirstDayOfWeek(new Date(), weekStartsOn)) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { shouldShow, previousGoals } = await checkWeekReviewStatus(
          userId,
          organizationId,
          currentWeekKey,
          previousWeekKey,
        );

        if (shouldShow) {
          setPreviousWeekGoals(previousGoals);
          setIsOpen(true);
        }
      } catch (error) {
        console.error("Failed to check week review status:", error);
      } finally {
        setLoading(false);
      }
    };

    checkReviewStatus();
  }, [userId, organizationId, currentWeekKey, previousWeekKey, weekStartsOn]);

  const onClose = React.useCallback(async () => {
    if (!userId || !organizationId) return;

    try {
      await markPreviousWeekReviewed(userId, organizationId, currentWeekKey);
    } catch (error) {
      console.error("Failed to mark previous week reviewed:", error);
    }

    setIsOpen(false);
  }, [userId, organizationId, currentWeekKey]);

  const onGoalToggle = React.useCallback(
    async (goalId, completed) => {
      if (!userId) return;

      const goal = previousWeekGoals.find((g) => g.id === goalId);
      if (!goal) return;

      setPreviousWeekGoals((prev) =>
        prev.map((g) => (g.id === goalId ? { ...g, completed } : g)),
      );

      try {
        await saveGoal(
          {
            id: goalId,
            text: goal.text,
            completed,
            organizationId: goal.organizationId,
            teamId: goal.teamId,
          },
          userId,
          previousWeekKey,
        );
      } catch (error) {
        console.error("Failed to toggle goal:", error);
        setPreviousWeekGoals((prev) =>
          prev.map((g) =>
            g.id === goalId ? { ...g, completed: !completed } : g,
          ),
        );
      }
    },
    [userId, previousWeekKey, previousWeekGoals],
  );

  const allComplete =
    previousWeekGoals.length > 0 && previousWeekGoals.every((g) => g.completed);

  return {
    isOpen,
    previousWeekGoals,
    previousWeekKey,
    loading,
    allComplete,
    onClose,
    onGoalToggle,
  };
}
